import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Ticket, Trash2, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { rm, fmtDate, toLocalInput, timeAgo } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { useInfiniteCatalogProducts } from "../hooks/useInfiniteCatalogProducts";
import { useSettingValue } from "../hooks/useSettingsDraft";
import {
  Button,
  Input,
  SearchSelect,
  Badge,
  statusColor,
  Toggle,
  Modal,
  PillSegmentSwitcher,
  useConfirm,
  parseDecimalField,
  parseIntField,
  filterNumericInput,
} from "../components/ui";
import { DataTable, type Column, type DataTableRowActionItem, type DataTableRowActions } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { useQueryRecordId } from "../lib/use-query-record";
import { showQueryLoader } from "../lib/query-utils";

type RaffleMode = "SINGLE" | "LOBBY";

interface CatalogProduct {
  id: string;
  name: string;
  price: number;
  stockAvailable: number;
  behavior?: string;
  images?: string[];
}

function winnerPriceFromProduct(product: CatalogProduct | undefined): number {
  return product?.price ?? 0;
}

function productOptionHint(
  product: CatalogProduct,
  raffleCommitted: Map<string, number> = new Map(),
): string | undefined {
  const parts = [rm(product.price)];
  const stock = stockOptionHint(product, raffleCommitted);
  if (stock) parts.push(stock);
  return parts.join(" · ");
}

interface StockStatus {
  tracksStock: boolean;
  available: number;
  winnersNeeded: number;
  isOver: boolean;
}

function productTracksStock(product: CatalogProduct): boolean {
  return product.behavior !== "CREDIT_PACK" && product.stockAvailable < 999_999;
}

function stockOptionHint(
  product: CatalogProduct,
  raffleCommitted: Map<string, number> = new Map(),
): string | undefined {
  if (!productTracksStock(product)) return "Unlimited";
  return `${product.stockAvailable + (raffleCommitted.get(product.id) ?? 0)} left`;
}

function getStockStatus(
  productId: string,
  winnersNeeded: number,
  productsById: Map<string, CatalogProduct>,
  enforceStockLimits: boolean,
  raffleCommitted: Map<string, number> = new Map(),
): StockStatus | null {
  const product = productsById.get(productId);
  if (!product) return null;
  const tracksStock = productTracksStock(product);
  const available = product.stockAvailable + (raffleCommitted.get(productId) ?? 0);
  return {
    tracksStock,
    available,
    winnersNeeded,
    isOver: enforceStockLimits && tracksStock && winnersNeeded > available,
  };
}

function lobbyWinnersByProduct(form: RaffleForm): Map<string, number> {
  const totals = new Map<string, number>();
  for (const item of form.lobbyItems) {
    const productId = item.productId.trim();
    if (!productId) continue;
    totals.set(productId, (totals.get(productId) ?? 0) + parseIntField(item.winnerCount, 1));
  }
  return totals;
}

function getStockValidationError(
  form: RaffleForm,
  productsById: Map<string, CatalogProduct>,
  enforceStockLimits: boolean,
  raffleCommitted: Map<string, number> = new Map(),
): string | null {
  if (!enforceStockLimits) return null;
  if (form.mode === "SINGLE") {
    const productId = form.productId.trim();
    if (!productId) return null;
    const status = getStockStatus(productId, 1, productsById, enforceStockLimits, raffleCommitted);
    if (status?.isOver) {
      const name = productsById.get(productId)?.name ?? "Product";
      return `${name} only has ${status.available} available (1 winner required)`;
    }
    return null;
  }

  const totals = lobbyWinnersByProduct(form);
  for (let i = 0; i < form.lobbyItems.length; i++) {
    const item = form.lobbyItems[i]!;
    const productId = item.productId.trim();
    if (!productId) continue;
    const winnersNeeded = totals.get(productId) ?? 0;
    const status = getStockStatus(productId, winnersNeeded, productsById, enforceStockLimits, raffleCommitted);
    if (status?.isOver) {
      const name = productsById.get(productId)?.name ?? "Product";
      return `Prize #${i + 1}: ${name} needs ${winnersNeeded} units but only ${status.available} available`;
    }
  }
  return null;
}

function StockAvailabilityHint({ status }: { status: StockStatus | null }) {
  if (!status) return null;
  if (!status.tracksStock) {
    return <p className="text-[10px] text-white/40">Stock not tracked for this product type</p>;
  }
  return (
    <p className={`text-[10px] tabular-nums ${status.isOver ? "font-medium text-red-400" : "text-white/45"}`}>
      {status.available} available
      {status.isOver && (
        <>
          {" "}
          · {status.winnersNeeded} winner{status.winnersNeeded === 1 ? "" : "s"} requested (
          {status.winnersNeeded - status.available} short)
        </>
      )}
    </p>
  );
}

interface RaffleItemRow {
  id?: string;
  productId: string;
  winnerCount: number;
  maxTicketsPerUser?: number | null;
  priceForWinner: number;
  ticketWindowSeconds?: number | null;
  status?: string;
  ticketCount?: number;
  product?: CatalogProduct | { id: string; name: string };
  winners?: { rank: number; ticketNo: number; userId: string; name: string }[];
}

function isRafflePreviewStatus(status: string) {
  return status === "DRAWN" || status === "COMPLETED";
}

/** Prize queue locked only after the raffle finishes or enters live draw. */
function canEditRafflePrizes(editing: RaffleRow | null): boolean {
  if (!editing) return true;
  return !["DRAWN", "COMPLETED", "CANCELLED", "DRAWING"].includes(editing.status);
}

function canEditRaffle(row: RaffleRow): boolean {
  return !["DRAWN", "COMPLETED", "CANCELLED", "DRAWING"].includes(row.status);
}

function canEditSingleProduct(editing: RaffleRow | null): boolean {
  if (!editing) return true;
  if (editing.ticketCount > 0) return false;
  return canEditRafflePrizes(editing);
}

function lobbyItemProductLocked(item: LobbyItemForm): boolean {
  return (item.ticketCount ?? 0) > 0 || (!!item.status && item.status !== "PENDING");
}

function lobbyItemWinnerCountLocked(item: LobbyItemForm): boolean {
  return !!item.status && item.status !== "PENDING";
}

function lobbyItemRemovable(item: LobbyItemForm, totalItems: number): boolean {
  if (totalItems <= 1) return false;
  return (item.ticketCount ?? 0) === 0 && (!item.status || item.status === "PENDING");
}

function monitorWinnerTicketNos(monitor: MonitorData): Set<number> {
  const nos = new Set<number>();
  if (monitor.raffle.winnerUserId) {
    const ticket = monitor.tickets.find((t) => t.userId === monitor.raffle.winnerUserId && !t.forfeited);
    if (ticket) nos.add(ticket.ticketNo);
  }
  for (const item of monitor.raffle.items ?? []) {
    for (const winner of item.winners ?? []) {
      nos.add(winner.ticketNo);
    }
  }
  return nos;
}

type MonitorTicket = MonitorData["tickets"][number];

function monitorTicketsByItemId(monitor: MonitorData): Map<string, MonitorTicket[]> {
  const map = new Map<string, MonitorTicket[]>();
  for (const ticket of monitor.tickets) {
    if (!ticket.raffleItemId) continue;
    const list = map.get(ticket.raffleItemId) ?? [];
    list.push(ticket);
    map.set(ticket.raffleItemId, list);
  }
  return map;
}

function initialExpandedDrawIds(monitor: MonitorData): Set<string> {
  const ids = new Set<string>();
  const items = monitor.raffle.items ?? [];
  const byItem = monitorTicketsByItemId(monitor);
  for (const [idx, item] of items.entries()) {
    if (!item.id) continue;
    if ((byItem.get(item.id)?.length ?? 0) > 0) ids.add(item.id);
    if (item.status === "ENTRY_OPEN" || item.status === "DRAWING") ids.add(item.id);
    if (monitor.raffle.status === "DRAWING" && monitor.raffle.currentItemIndex === idx) ids.add(item.id);
  }
  return ids;
}

interface RaffleRow {
  id: string;
  title: string;
  status: string;
  mode: RaffleMode;
  ticketCount: number;
  itemCount?: number;
  product: CatalogProduct | { id: string; name: string } | null;
  items?: RaffleItemRow[];
  entryOpensAt: string;
  entryClosesAt: string;
  maxTicketsPerUser: number;
  entryCostCredits: number;
  priceForWinner: number;
  paymentDeadlineHours: number;
  redrawOnForfeit: boolean;
  drawDelaySeconds: number;
  interItemDelaySeconds: number;
  description?: string | null;
}

/** Units already reserved for the raffle being edited — add back when checking stock. */
function raffleCommittedByProduct(editing: RaffleRow | null | undefined): Map<string, number> {
  const totals = new Map<string, number>();
  if (!editing) return totals;
  if (editing.mode === "SINGLE") {
    const productId = editing.product && "id" in editing.product ? editing.product.id : "";
    if (productId) totals.set(productId, 1);
    return totals;
  }
  for (const item of editing.items ?? []) {
    const productId = item.productId || (item.product && "id" in item.product ? item.product.id : "");
    if (!productId) continue;
    totals.set(productId, (totals.get(productId) ?? 0) + item.winnerCount);
  }
  return totals;
}

type RaffleProductRef = CatalogProduct | { id: string; name: string };

function raffleProductToCatalog(product: RaffleProductRef): CatalogProduct {
  const extended = product as CatalogProduct;
  return {
    id: product.id,
    name: product.name,
    price: extended.price ?? 0,
    stockAvailable: extended.stockAvailable ?? 0,
    behavior: extended.behavior,
    images: extended.images,
  };
}

/** Products referenced by the open editor form — merged into SearchSelect when not in the catalog page. */
function collectRaffleProducts(source: Pick<RaffleRow, "product" | "items">): CatalogProduct[] {
  const byId = new Map<string, CatalogProduct>();
  const add = (product: RaffleProductRef | null | undefined) => {
    if (!product?.id || !product.name) return;
    byId.set(product.id, raffleProductToCatalog(product));
  };
  add(source.product);
  for (const item of source.items ?? []) {
    add(item.product);
  }
  return [...byId.values()];
}

interface LobbyItemForm {
  key: string;
  id?: string;
  productId: string;
  winnerCount: string;
  maxTicketsPerUser: string;
  priceForWinner: string;
  ticketWindowSeconds: string;
  ticketCount?: number;
  status?: string;
}

interface RaffleForm {
  title: string;
  mode: RaffleMode;
  productId: string;
  lobbyItems: LobbyItemForm[];
  entryOpensAt: string;
  entryClosesAt: string;
  maxTicketsPerUser: string;
  entryCostCredits: string;
  priceForWinner: string;
  paymentDeadlineHours: string;
  redrawOnForfeit: boolean;
  drawDelaySeconds: string;
  interItemDelaySeconds: string;
}

interface MonitorData {
  raffle: {
    id: string;
    title: string;
    status: string;
    mode: RaffleMode;
    productName: string;
    winnerUserId?: string | null;
    drawnAt?: string | null;
    currentItemIndex?: number;
    items?: RaffleItemRow[];
  };
  ticketCount: number;
  maxTicketNo?: number;
  viewers: number;
  tickets: {
    ticketNo: number;
    userId: string;
    name: string;
    email: string;
    forfeited: boolean;
    raffleItemId: string | null;
    at: string;
  }[];
}

let itemKeySeq = 0;
function newItemKey() {
  return `item-${++itemKeySeq}`;
}

const ENTRY_CLOSE_PRESETS = [
  { label: "30 sec", ms: 30_000 },
  { label: "1 min", ms: 60_000 },
  { label: "5 min", ms: 5 * 60_000 },
  { label: "15 min", ms: 15 * 60_000 },
  { label: "30 min", ms: 30 * 60_000 },
  { label: "1 hour", ms: 60 * 60_000 },
  { label: "24 hours", ms: 24 * 60 * 60_000 },
  { label: "7 days", ms: 7 * 24 * 60 * 60_000 },
] as const;

function entryCloseFromPreset(entryOpensAt: string, ms: number): string {
  const base = entryOpensAt ? new Date(entryOpensAt) : new Date();
  const anchor = Number.isNaN(base.getTime()) ? new Date() : base;
  return toLocalInput(new Date(anchor.getTime() + ms));
}

const emptyForm = (): RaffleForm => ({
  title: "",
  mode: "SINGLE",
  productId: "",
  lobbyItems: [
    {
      key: newItemKey(),
      productId: "",
      winnerCount: "1",
      maxTicketsPerUser: "",
      priceForWinner: "0",
      ticketWindowSeconds: "",
    },
  ],
  entryOpensAt: toLocalInput(new Date()),
  entryClosesAt: toLocalInput(new Date(Date.now() + 7 * 86400000)),
  maxTicketsPerUser: "1",
  entryCostCredits: "0",
  priceForWinner: "0",
  paymentDeadlineHours: "48",
  redrawOnForfeit: true,
  drawDelaySeconds: "30",
  interItemDelaySeconds: "30",
});

function formToBody(form: RaffleForm) {
  const priceForWinner = parseDecimalField(form.priceForWinner, 0);
  const base = {
    title: form.title.trim(),
    mode: form.mode,
    entryOpensAt: new Date(form.entryOpensAt).toISOString(),
    entryClosesAt: new Date(form.entryClosesAt).toISOString(),
    maxTicketsPerUser: parseIntField(form.maxTicketsPerUser, 1),
    entryCostCredits: parseIntField(form.entryCostCredits, 0),
    paymentDeadlineHours: parseIntField(form.paymentDeadlineHours, 48),
    redrawOnForfeit: form.redrawOnForfeit,
    drawDelaySeconds: parseIntField(form.drawDelaySeconds, 30),
    interItemDelaySeconds: parseIntField(form.interItemDelaySeconds, 0),
  };
  if (form.mode === "SINGLE") {
    return { ...base, mode: "SINGLE" as const, productId: form.productId.trim(), priceForWinner };
  }
  return {
    ...base,
    mode: "LOBBY" as const,
    priceForWinner,
    items: form.lobbyItems.map((i) => ({
      ...(i.id ? { id: i.id } : {}),
      productId: i.productId.trim(),
      winnerCount: parseIntField(i.winnerCount, 1),
      maxTicketsPerUser: i.maxTicketsPerUser.trim()
        ? parseIntField(i.maxTicketsPerUser, parseIntField(form.maxTicketsPerUser, 1))
        : null,
      priceForWinner: i.priceForWinner.trim() ? parseDecimalField(i.priceForWinner, priceForWinner) : priceForWinner,
      ticketWindowSeconds: i.ticketWindowSeconds.trim()
        ? parseIntField(i.ticketWindowSeconds, parseIntField(form.drawDelaySeconds, 30))
        : null,
    })),
  };
}

/** Client-side guard before POST/PUT — returns a friendly message or null if OK. */
function validateRaffleForm(
  form: RaffleForm,
  productIds?: Set<string>,
  productsById?: Map<string, CatalogProduct>,
  enforceStockLimits = true,
  raffleCommitted: Map<string, number> = new Map(),
): string | null {
  if (form.mode === "SINGLE") {
    if (!form.productId.trim()) return "Select a product for this raffle";
    if (productIds && !productIds.has(form.productId.trim())) return "Selected product is no longer available — pick again";
  } else {
    if (form.lobbyItems.length === 0) return "Add at least one prize to the queue";
    for (let i = 0; i < form.lobbyItems.length; i++) {
      const pid = form.lobbyItems[i]!.productId.trim();
      if (!pid) return `Select a product for prize #${i + 1}`;
      if (productIds && !productIds.has(pid)) return `Product for prize #${i + 1} is no longer available — pick again`;
      const winRaw = form.lobbyItems[i]!.ticketWindowSeconds.trim();
      if (winRaw) {
        const winSec = parseIntField(winRaw, 0);
        if (winSec < 30) return `Ticket window for prize #${i + 1} must be at least 30 seconds`;
      }
    }
  }
  if (productsById) {
    const stockError = getStockValidationError(form, productsById, enforceStockLimits, raffleCommitted);
    if (stockError) return stockError;
  }
  return null;
}

export function Raffles() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const canWrite = useAuth(
    (s) => !!s.user && (s.user.isSuperadmin || s.user.permissions.includes("raffles.write")),
  );
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [prizeQueueModalOpen, setPrizeQueueModalOpen] = useState(false);
  const [queueFocusKey, setQueueFocusKey] = useState<string | null>(null);
  const queueModalOpenedRef = useRef(false);
  const winnerInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const priceInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [monitorId, setMonitorId] = useState<string | null>(null);
  const [expandedDraws, setExpandedDraws] = useState<Set<string>>(() => new Set());
  const expandedDrawsInitRef = useRef<string | null>(null);
  const [editing, setEditing] = useState<RaffleRow | null>(null);
  const [form, setForm] = useState<RaffleForm>(emptyForm);
  const [editorProducts, setEditorProducts] = useState<CatalogProduct[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const { recordId, clearRecordId } = useQueryRecordId();

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 250);
    return () => globalThis.clearTimeout(t);
  }, [productSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ["raffles"],
    queryFn: () => api<{ items: RaffleRow[] }>("/raffles?all=true"),
  });

  useEffect(() => {
    if (!recordId || !data?.items) return;
    if (data.items.some((item) => item.id === recordId)) {
      setMonitorId(recordId);
      clearRecordId();
    }
  }, [recordId, data?.items, clearRecordId]);

  const productsQuery = useInfiniteCatalogProducts({
    enabled: editorOpen,
    search: debouncedProductSearch,
    queryKey: ["raffle-mechanics"],
    params: { forMechanics: "true" },
  });

  const allowNegativeStock = useSettingValue("inventory.allow_negative_stock", false, { enabled: editorOpen });

  const enforceStockLimits = !allowNegativeStock;

  const raffleCommitted = useMemo(() => raffleCommittedByProduct(editing), [editing]);

  const prizeQueueEditable = canEditRafflePrizes(editing);
  const singleProductEditable = canEditSingleProduct(editing);

  const productsById = useMemo(() => {
    const byId = new Map<string, CatalogProduct>();
    for (const product of editorProducts) byId.set(product.id, product);
    for (const product of productsQuery.items) byId.set(product.id, product as CatalogProduct);
    return byId;
  }, [editorProducts, productsQuery.items]);

  const productOptions = useMemo(
    () =>
      [...productsById.values()]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({
          value: p.id,
          label: p.name,
          hint: productOptionHint(p, raffleCommitted),
          image: p.images?.[0] ?? null,
        })),
    [productsById, raffleCommitted],
  );

  const productIdSet = useMemo(() => new Set(productsById.keys()), [productsById]);

  const lobbyWinnerTotals = useMemo(
    () => (form.mode === "LOBBY" ? lobbyWinnersByProduct(form) : new Map<string, number>()),
    [form.mode, form.lobbyItems],
  );

  const singleStockStatus = useMemo(() => {
    if (form.mode !== "SINGLE" || !form.productId.trim()) return null;
    return getStockStatus(form.productId.trim(), 1, productsById, enforceStockLimits, raffleCommitted);
  }, [form.mode, form.productId, productsById, enforceStockLimits, raffleCommitted]);

  const hasStockIssue = useMemo(
    () => getStockValidationError(form, productsById, enforceStockLimits, raffleCommitted) !== null,
    [form, productsById, enforceStockLimits, raffleCommitted],
  );

  const { data: monitor, isLoading: monitorLoading } = useQuery({
    queryKey: ["raffle-monitor", monitorId],
    queryFn: () => api<MonitorData>(`/raffles/${monitorId}/monitor`),
    enabled: !!monitorId,
    refetchInterval: (query) => {
      const status = query.state.data?.raffle.status;
      if (!monitorId || (status && isRafflePreviewStatus(status))) return false;
      return 5000;
    },
  });

  useEffect(() => {
    expandedDrawsInitRef.current = null;
    setExpandedDraws(new Set());
  }, [monitorId]);

  useEffect(() => {
    if (!monitor || monitor.raffle.mode !== "LOBBY" || !monitorId) return;
    if (expandedDrawsInitRef.current === monitorId) return;
    expandedDrawsInitRef.current = monitorId;
    setExpandedDraws(initialExpandedDrawIds(monitor));
  }, [monitor, monitorId]);

  const saveMut = useMutation({
    mutationFn: (payload: { id?: string; body: ReturnType<typeof formToBody> }) => {
      if (payload.id) return api(`/raffles/${payload.id}`, { method: "PUT", body: payload.body });
      return api<{ id: string }>("/raffles", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["raffles"] });
      toast("success", editing ? "Raffle updated" : "Raffle created");
      closeEditor();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const drawMut = useMutation({
    mutationFn: (id: string) => api(`/raffles/${id}/draw`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["raffles"] });
      toast("success", "Starting countdown — draw runs when the window ends");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Draw failed"),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => api(`/raffles/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["raffles"] });
      toast("success", "Raffle cancelled");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Cancel failed"),
  });

  function openCreate() {
    setEditing(null);
    setEditorProducts([]);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openPreview(row: RaffleRow) {
    closeEditor();
    setMonitorId(row.id);
  }

  function openRow(row: RaffleRow) {
    if (isRafflePreviewStatus(row.status)) {
      if (can("raffles.monitor")) openPreview(row);
      return;
    }
    if (can("raffles.write")) openEdit(row);
  }

  function openEdit(row: RaffleRow) {
    if (!canEditRaffle(row)) return;
    setEditing(row);
    setEditorProducts(collectRaffleProducts(row));
    setForm({
      title: row.title,
      mode: row.mode ?? "SINGLE",
      productId: row.product?.id ?? "",
      lobbyItems:
        row.mode === "LOBBY" && row.items?.length
          ? row.items.map((i) => ({
              key: newItemKey(),
              id: i.id,
              productId: i.product?.id ?? i.productId,
              winnerCount: String(i.winnerCount),
              maxTicketsPerUser: i.maxTicketsPerUser != null ? String(i.maxTicketsPerUser) : "",
              priceForWinner: String(i.priceForWinner),
              ticketWindowSeconds:
                i.ticketWindowSeconds != null ? String(i.ticketWindowSeconds) : "",
              ticketCount: i.ticketCount,
              status: i.status,
            }))
          : [
              {
                key: newItemKey(),
                productId: "",
                winnerCount: "1",
                maxTicketsPerUser: "",
                priceForWinner: "0",
                ticketWindowSeconds: "",
              },
            ],
      entryOpensAt: toLocalInput(row.entryOpensAt),
      entryClosesAt: toLocalInput(row.entryClosesAt),
      maxTicketsPerUser: String(row.maxTicketsPerUser),
      entryCostCredits: String(row.entryCostCredits),
      priceForWinner: String(row.priceForWinner),
      paymentDeadlineHours: String(row.paymentDeadlineHours),
      redrawOnForfeit: row.redrawOnForfeit,
      drawDelaySeconds: String(row.drawDelaySeconds ?? 30),
      interItemDelaySeconds: String(row.interItemDelaySeconds ?? 30),
    });
    setEditorOpen(true);
  }

  async function openDuplicate(row: RaffleRow) {
    setEditing(null);
    try {
      const detail = await api<{ raffle: RaffleRow }>(`/raffles/${row.id}`);
      const source = detail.raffle;
      setEditorProducts(collectRaffleProducts(source));
      const warnings: string[] = [];
      const scheduleBase = {
        title: `${source.title} (copy)`,
        entryOpensAt: toLocalInput(new Date(Date.now() + 3600000)),
        entryClosesAt: toLocalInput(new Date(Date.now() + 7 * 86400000)),
        maxTicketsPerUser: String(source.maxTicketsPerUser),
        entryCostCredits: String(source.entryCostCredits),
        priceForWinner: String(source.priceForWinner),
        paymentDeadlineHours: String(source.paymentDeadlineHours),
        redrawOnForfeit: source.redrawOnForfeit,
        drawDelaySeconds: String(source.drawDelaySeconds ?? 30),
        interItemDelaySeconds: String(source.interItemDelaySeconds ?? 30),
      };

      if (source.mode === "LOBBY") {
        const lobbyItems =
          source.items?.length
            ? source.items.map((i) => ({
                key: newItemKey(),
                productId: i.product?.id ?? i.productId ?? "",
                winnerCount: "1",
                maxTicketsPerUser: i.maxTicketsPerUser != null ? String(i.maxTicketsPerUser) : "",
                priceForWinner: String(i.priceForWinner ?? source.priceForWinner),
                ticketWindowSeconds:
                  i.ticketWindowSeconds != null ? String(i.ticketWindowSeconds) : "",
              }))
            : [
                {
                  key: newItemKey(),
                  productId: "",
                  winnerCount: "1",
                  maxTicketsPerUser: "",
                  priceForWinner: String(source.priceForWinner),
                  ticketWindowSeconds: "",
                },
              ];

        if (!source.items?.length) warnings.push("Original had no prizes — add a product before saving");

        lobbyItems.forEach((item, index) => {
          if (!item.productId.trim()) warnings.push(`Prize #${index + 1}: select a product`);
        });

        setForm({ ...scheduleBase, mode: "LOBBY", productId: "", lobbyItems });
      } else {
        const productId = source.product && "id" in source.product ? source.product.id : "";
        if (!productId) warnings.push("Select a product before saving");
        setForm({
          ...scheduleBase,
          mode: "SINGLE",
          productId,
          lobbyItems: emptyForm().lobbyItems,
        });
      }

      setEditorOpen(true);
      if (warnings.length) toast("error", warnings.join(" · "));
    } catch (e) {
      toast("error", e instanceof ApiError ? e.message : "Could not duplicate raffle");
    }
  }

  function submitForm() {
    const validationError = validateRaffleForm(form, productIdSet, productsById, enforceStockLimits, raffleCommitted);
    if (validationError) {
      toast("error", validationError);
      return;
    }
    saveMut.mutate({ id: editing?.id, body: formToBody(form) });
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setEditorProducts([]);
    setPrizeQueueModalOpen(false);
  }

  const canSave =
    form.title.trim().length >= 3 &&
    !hasStockIssue &&
    (form.mode === "SINGLE"
      ? !!form.productId.trim()
      : form.lobbyItems.length > 0 && form.lobbyItems.every((i) => i.productId.trim()));

  useBottomBarActions(() => {
    if (!canWrite || monitorId) return [];
    if (editorOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: editing ? "Save" : "Create",
          variant: "primary" as const,
          disabled: saveMut.isPending || !canSave,
          onClick: submitForm,
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  function setField<K extends keyof RaffleForm>(key: K, value: RaffleForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function addLobbyItem() {
    setForm((f) => ({
      ...f,
      lobbyItems: [
        ...f.lobbyItems,
        {
          key: newItemKey(),
          productId: "",
          winnerCount: "1",
          maxTicketsPerUser: "",
          priceForWinner: f.priceForWinner,
          ticketWindowSeconds: "",
        },
      ],
    }));
  }

  function addLobbyItemWithFocus() {
    const key = newItemKey();
    setForm((f) => ({
      ...f,
      lobbyItems: [
        ...f.lobbyItems,
        {
          key,
          productId: "",
          winnerCount: "1",
          maxTicketsPerUser: "",
          priceForWinner: f.priceForWinner,
          ticketWindowSeconds: "",
        },
      ],
    }));
    setQueueFocusKey(key);
  }

  useEffect(() => {
    if (!prizeQueueModalOpen) {
      queueModalOpenedRef.current = false;
      setQueueFocusKey(null);
      return;
    }
    if (queueModalOpenedRef.current || editing) return;
    queueModalOpenedRef.current = true;

    if (form.lobbyItems.length === 0) {
      const key = newItemKey();
      setForm((f) => ({
        ...f,
        lobbyItems: [
          {
            key,
            productId: "",
            winnerCount: "1",
            maxTicketsPerUser: "",
            priceForWinner: f.priceForWinner,
            ticketWindowSeconds: "",
          },
        ],
      }));
      setQueueFocusKey(key);
      return;
    }

    const firstEmpty = form.lobbyItems.find((i) => !i.productId.trim());
    setQueueFocusKey(firstEmpty?.key ?? form.lobbyItems[form.lobbyItems.length - 1]!.key);
  }, [prizeQueueModalOpen, editing]);

  function removeLobbyItem(key: string) {
    setForm((f) => ({
      ...f,
      lobbyItems: f.lobbyItems.length > 1 ? f.lobbyItems.filter((i) => i.key !== key) : f.lobbyItems,
    }));
  }

  function updateLobbyItem(key: string, patch: Partial<LobbyItemForm>) {
    setForm((f) => ({
      ...f,
      lobbyItems: f.lobbyItems.map((i) => (i.key === key ? { ...i, ...patch } : i)),
    }));
  }

  function setSingleProduct(productId: string) {
    const product = productsById.get(productId);
    setForm((f) => ({
      ...f,
      productId,
      priceForWinner: product ? String(winnerPriceFromProduct(product)) : f.priceForWinner,
    }));
  }

  function setLobbyProduct(itemKey: string, productId: string) {
    const product = productsById.get(productId);
    updateLobbyItem(itemKey, {
      productId,
      priceForWinner: product ? String(winnerPriceFromProduct(product)) : undefined,
    });
    requestAnimationFrame(() => winnerInputRefs.current.get(itemKey)?.focus());
  }

  const columns: Column<RaffleRow>[] = [
    {
      key: "title",
      header: "Title",
      sortable: true,
      sortValue: (r) => r.title,
      render: (r) => (
        <div>
          <span className="font-medium">{r.title}</span>
          {r.mode === "LOBBY" && (
            <span className="ml-2 text-[10px] text-white/40">Lobby · {r.itemCount ?? r.items?.length ?? 0} items</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "product",
      header: "Prize",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.product?.name ?? "",
      render: (r) =>
        r.mode === "LOBBY" ? (
          <span className="text-xs text-white/60">{r.itemCount ?? r.items?.length ?? 0} queued items</span>
        ) : (
          (r.product?.name ?? "—")
        ),
    },
    {
      key: "tickets",
      header: "Tickets",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.ticketCount,
      render: (r) => <span className="tabular-nums">{r.ticketCount}</span>,
    },
    {
      key: "closes",
      header: "Closes",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.entryClosesAt,
      exportValue: (r) => fmtDate(r.entryClosesAt),
      render: (r) => <span className="text-xs text-white/60">{fmtDate(r.entryClosesAt)}</span>,
    },
  ];

  const raffleRowActions = useMemo((): DataTableRowActions<RaffleRow> => ({
    onDuplicate: canWrite ? (r) => void openDuplicate(r) : undefined,
    extra: (r) => {
      const items: DataTableRowActionItem[] = [];
      if (canWrite && canEditRaffle(r)) {
        items.push({ label: "Edit", onClick: () => openEdit(r) });
      }
      if (can("raffles.monitor")) {
        items.push({
          label: isRafflePreviewStatus(r.status) ? "Preview" : "Monitor",
          onClick: () => openPreview(r),
        });
      }
      if (can("raffles.draw") && ["OPEN", "SCHEDULED"].includes(r.status)) {
        items.push({
          label: "Draw",
          onClick: () =>
            confirm({
              title: "Draw raffle now?",
              message:
                "A final ticket window opens for the configured starting countdown. The draw runs (or closes if no entries) when it ends.",
              confirmLabel: "Draw",
              action: () => drawMut.mutateAsync(r.id),
            }),
        });
      }
      if (can("raffles.write") && !["COMPLETED", "CANCELLED", "DRAWN"].includes(r.status)) {
        items.push({
          label: "Cancel",
          variant: "danger",
          onClick: () =>
            confirm({
              title: "Cancel raffle?",
              message: "This cannot be undone.",
              confirmLabel: "Cancel raffle",
              danger: true,
              action: () => cancelMut.mutateAsync(r.id),
            }),
        });
      }
      return items;
    },
  }), [can, confirm, drawMut, cancelMut]);

  return (
    <ListPageShell>
      {dialog}

      <DataTable
        columns={columns}
        rows={data?.items}
        rowKey={(r) => r.id}
        loading={showQueryLoader({ isLoading, data })}
        exportFilename="raffles"
        onRowClick={can("raffles.write") || can("raffles.monitor") ? openRow : undefined}
        activeKey={editing?.id ?? monitorId ?? undefined}
        rowActions={raffleRowActions}
        empty={{
          icon: Ticket,
          title: "No raffles yet",
          hint: canWrite ? "Create a raffle listing with prizes and entry rules." : undefined,
          action: canWrite ? (
            <Button size="sm" onClick={openCreate}>
              <Plus size={14} /> Create
            </Button>
          ) : undefined,
        }}
      />

      <RightSidebarPanel
        open={editorOpen}
        wide
        title={editing ? "Edit raffle" : "New raffle"}
        subtitle={form.mode === "LOBBY" ? "Multi-item lobby" : editing?.product?.name}
        onClose={closeEditor}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saveMut.isPending || !canSave}
              onClick={submitForm}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      >
        <div className="sidebar-form">
          <Input label="Title" value={form.title} onChange={(e) => setField("title", e.target.value)} required />

          {!editing && (
            <div>
              <span className="mb-1.5 block text-xs font-medium text-white/60">Format</span>
              <PillSegmentSwitcher
                options={[
                  { value: "SINGLE" as RaffleMode, label: "Single prize" },
                  { value: "LOBBY" as RaffleMode, label: "Multi-item lobby" },
                ]}
                value={form.mode}
                onChange={(v) => setField("mode", v)}
              />
            </div>
          )}

          {form.mode === "SINGLE" ? (
            <>
              <SearchSelect
                label="Product"
                placeholder="Select product…"
                searchPlaceholder="Search products…"
                options={productOptions}
                value={form.productId}
                onChange={setSingleProduct}
                onSearchChange={setProductSearch}
                serverFiltered
                hasMore={productsQuery.hasNextPage}
                loadingMore={productsQuery.isFetchingNextPage}
                onLoadMore={productsQuery.fetchNextPage}
                disabled={!singleProductEditable}
              />
              <StockAvailabilityHint status={singleStockStatus} />
              {singleStockStatus?.isOver && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  Not enough stock for this prize — only {singleStockStatus.available} unit
                  {singleStockStatus.available === 1 ? "" : "s"} available.
                </p>
              )}
              <Input
                label="Price for winner (RM)"
                type="number"
                min={0}
                step="0.01"
                hint={
                  form.productId && productsById.get(form.productId)
                    ? `Filled from product price (${rm(productsById.get(form.productId)!.price)}). Override if winners pay a different amount. Use 0 for shipping only.`
                    : "Amount the winner pays to claim the prize (before shipping). Auto-fills from product price when selected."
                }
                value={form.priceForWinner}
                onChange={(e) => setField("priceForWinner", e.target.value)}
              />
            </>
          ) : (
            <section className="space-y-3">
              <div className="rounded-xl border border-line bg-white/[0.02] p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-white/40">Prize queue</p>
                    <p className="mt-1 text-xs text-white/55">
                      {form.lobbyItems.length} item{form.lobbyItems.length === 1 ? "" : "s"} · drawn in order
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setPrizeQueueModalOpen(true)}>
                    Manage queue
                  </Button>
                </div>
                {!prizeQueueEditable && (
                  <p className="text-xs text-white/45">
                    Prize queue is locked while the raffle is drawing or after it finishes.
                  </p>
                )}
                {prizeQueueEditable && editing && editing.ticketCount > 0 && (
                  <p className="text-xs text-white/45">
                    Collectors have entered — you can reorder, change prices, adjust winner counts, and remove
                    undrawn prizes without tickets. Product swaps are locked on rows that already have entries.
                  </p>
                )}
                {hasStockIssue && prizeQueueEditable && (
                  <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                    One or more prizes exceed available stock — open Manage queue to fix.
                  </p>
                )}
                {form.lobbyItems.length > 0 ? (
                  <div className="scrollbar-visible max-h-48 overflow-y-auto overflow-x-auto rounded-lg border border-line">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="sticky top-0 z-10 border-b border-line bg-surface-2 text-[10px] uppercase tracking-wider text-white/40">
                          <th className="px-2 py-1.5 font-medium">#</th>
                          <th className="px-2 py-1.5 font-medium">Product</th>
                          <th className="px-2 py-1.5 text-right font-medium">Winners</th>
                          <th className="hidden px-2 py-1.5 text-right font-medium sm:table-cell">Window</th>
                          <th className="hidden px-2 py-1.5 text-right font-medium sm:table-cell">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.lobbyItems.map((item, index) => {
                          const product = productsById.get(item.productId.trim());
                          const windowSec = item.ticketWindowSeconds.trim()
                            ? item.ticketWindowSeconds
                            : form.drawDelaySeconds || "30";
                          return (
                            <tr key={item.key} className="border-b border-line/60 last:border-0">
                              <td className="px-2 py-1.5 font-mono text-brand-pink/80">{index + 1}</td>
                              <td className="max-w-[8rem] truncate px-2 py-1.5 text-white/75">{product?.name ?? "—"}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-white/60">{item.winnerCount}</td>
                              <td className="hidden px-2 py-1.5 text-right tabular-nums text-white/60 sm:table-cell">
                                {windowSec}s
                                {!item.ticketWindowSeconds.trim() ? (
                                  <span className="block text-[9px] text-white/35">default</span>
                                ) : null}
                              </td>
                              <td className="hidden px-2 py-1.5 text-right tabular-nums text-white/60 sm:table-cell">
                                {rm(parseDecimalField(item.priceForWinner, parseDecimalField(form.priceForWinner, 0)))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-white/45">No prizes yet. Open Manage queue to add items.</p>
                )}
              </div>
              <Input
                label="Default price for winner (RM)"
                type="number"
                min={0}
                step="0.01"
                hint="Fallback when a row leaves price blank. Auto-fills from product price in the queue editor."
                value={form.priceForWinner}
                onChange={(e) => setField("priceForWinner", e.target.value)}
              />
            </section>
          )}

          <div className="form-row-2">
            <Input
              label="Entry opens"
              type="datetime-local"
              value={form.entryOpensAt}
              onChange={(e) => setField("entryOpensAt", e.target.value)}
            />
            <Input
              label="Entry closes"
              type="datetime-local"
              value={form.entryClosesAt}
              onChange={(e) => setField("entryClosesAt", e.target.value)}
            />
          </div>
          <p className="text-[10px] text-white/40">Quick picks set the window length from entry opens</p>
          <div className="flex flex-wrap gap-1.5">
            {ENTRY_CLOSE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setField("entryClosesAt", entryCloseFromPreset(form.entryOpensAt, preset.ms))}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {form.mode === "LOBBY" && (
            <div className="form-row-2">
              <Input
                label="Default ticket window (sec)"
                type="number"
                min={30}
                hint="Fallback per-prize window when a queue row leaves Window blank (min 30)"
                value={form.drawDelaySeconds}
                onChange={(e) => setField("drawDelaySeconds", e.target.value)}
              />
              <Input
                label="Between items (sec)"
                type="number"
                min={0}
                hint="Pause after a draw before the next prize's ticket window"
                value={form.interItemDelaySeconds}
                onChange={(e) => setField("interItemDelaySeconds", e.target.value)}
              />
            </div>
          )}

          <div className="form-row-3">
            <Input
              label="Max tickets per user"
              type="number"
              min={1}
              value={form.maxTicketsPerUser}
              onChange={(e) => setField("maxTicketsPerUser", e.target.value)}
            />
            <Input
              label="Entry cost (credits)"
              type="number"
              min={0}
              step="0.01"
              value={form.entryCostCredits}
              onChange={(e) => setField("entryCostCredits", e.target.value)}
            />
            <Input
              label="Payment deadline (hours)"
              type="number"
              min={1}
              value={form.paymentDeadlineHours}
              onChange={(e) => setField("paymentDeadlineHours", e.target.value)}
            />
          </div>
          {form.mode === "SINGLE" && (
            <Toggle
              label="Redraw on forfeit"
              checked={form.redrawOnForfeit}
              onChange={(v) => setField("redrawOnForfeit", v)}
            />
          )}
        </div>
      </RightSidebarPanel>

      <Modal
        open={prizeQueueModalOpen && editorOpen && form.mode === "LOBBY"}
        size="lg"
        title="Prize queue"
        subtitle="Items are drawn in order. Search a product, Tab to winners and price, Enter on price to add the next row."
        onClose={() => setPrizeQueueModalOpen(false)}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            {prizeQueueEditable && (
              <Button variant="outline" size="sm" onClick={addLobbyItemWithFocus}>
                <Plus size={14} className="mr-1 inline" />
                Add item
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPrizeQueueModalOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        }
      >
        {form.lobbyItems.length === 0 ? (
          <p className="py-8 text-center text-sm text-white/50">No prizes in the queue yet. Add an item to get started.</p>
        ) : (
          <div className="scrollbar-visible max-h-[min(50vh,24rem)] overflow-x-scroll overflow-y-auto rounded-xl border border-line">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="sticky top-0 z-10 border-b border-line bg-surface-2 text-[10px] uppercase tracking-wider text-white/45">
                  <th className="w-10 px-3 py-2.5 font-medium">#</th>
                  <th className="min-w-[12rem] px-3 py-2.5 font-medium">Product</th>
                  <th className="w-24 px-3 py-2.5 font-medium">Stock</th>
                  <th className="w-24 px-3 py-2.5 font-medium">Winners</th>
                  <th className="w-24 px-3 py-2.5 font-medium">Max tickets</th>
                  <th className="w-24 px-3 py-2.5 font-medium">Window (sec)</th>
                  <th className="w-32 px-3 py-2.5 font-medium">Winner price</th>
                  {prizeQueueEditable && <th className="w-12 px-3 py-2.5" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {form.lobbyItems.map((item, index) => {
                  const productId = item.productId.trim();
                  const winnersNeeded = productId ? (lobbyWinnerTotals.get(productId) ?? 0) : 0;
                  const rowStockStatus = productId
                    ? getStockStatus(productId, winnersNeeded, productsById, enforceStockLimits, raffleCommitted)
                    : null;
                  const productLocked = !prizeQueueEditable || lobbyItemProductLocked(item);
                  const winnerCountLocked = !prizeQueueEditable || lobbyItemWinnerCountLocked(item);
                  const removable = prizeQueueEditable && lobbyItemRemovable(item, form.lobbyItems.length);

                  return (
                    <tr
                      key={item.key}
                      className={`border-b border-line/70 last:border-0 ${
                        rowStockStatus?.isOver ? "bg-red-500/[0.06]" : "hover:bg-white/[0.02]"
                      }`}
                    >
                      <td className="px-3 py-2 align-top font-mono text-xs text-brand-pink">{index + 1}</td>
                      <td className="px-3 py-2 align-top">
                        <SearchSelect
                          placeholder="Select product…"
                          searchPlaceholder="Search product…"
                          options={productOptions}
                          value={item.productId}
                          onChange={(v) => setLobbyProduct(item.key, v)}
                          onSearchChange={setProductSearch}
                          serverFiltered
                          hasMore={productsQuery.hasNextPage}
                          loadingMore={productsQuery.isFetchingNextPage}
                          onLoadMore={productsQuery.fetchNextPage}
                          disabled={productLocked}
                          autoOpenSearch={prizeQueueEditable && !productLocked && queueFocusKey === item.key}
                          onAutoOpenSearchHandled={() => {
                            if (queueFocusKey === item.key) setQueueFocusKey(null);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <StockAvailabilityHint status={rowStockStatus} />
                        {rowStockStatus?.isOver && (
                          <p className="mt-1 text-[10px] leading-snug text-red-400">
                            {rowStockStatus.winnersNeeded} needed, {rowStockStatus.available} available
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          ref={(el) => {
                            if (el) winnerInputRefs.current.set(item.key, el);
                            else winnerInputRefs.current.delete(item.key);
                          }}
                          type="number"
                          min={1}
                          max={100}
                          disabled={winnerCountLocked}
                          value={item.winnerCount}
                          onChange={(e) =>
                            updateLobbyItem(item.key, {
                              winnerCount: filterNumericInput(e.target.value),
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              priceInputRefs.current.get(item.key)?.focus();
                            }
                          }}
                          aria-label={`Winners for prize #${index + 1}`}
                          className={`w-full rounded-lg border bg-white/5 px-2.5 py-2 text-sm text-white outline-none focus:border-brand-pink/60 ${
                            rowStockStatus?.isOver ? "border-red-500/50" : "border-white/10"
                          }`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="number"
                          min={1}
                          max={100}
                          disabled={winnerCountLocked || (item.ticketCount ?? 0) > 0}
                          value={item.maxTicketsPerUser}
                          placeholder={form.maxTicketsPerUser || "1"}
                          onChange={(e) =>
                            updateLobbyItem(item.key, {
                              maxTicketsPerUser: filterNumericInput(e.target.value),
                            })
                          }
                          aria-label={`Max tickets per user for prize #${index + 1}`}
                          className="w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-brand-pink/60"
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="number"
                          min={30}
                          max={86400}
                          disabled={winnerCountLocked}
                          value={item.ticketWindowSeconds}
                          placeholder={form.drawDelaySeconds || "30"}
                          title="Blank uses default ticket window from raffle settings"
                          onChange={(e) =>
                            updateLobbyItem(item.key, {
                              ticketWindowSeconds: filterNumericInput(e.target.value),
                            })
                          }
                          aria-label={`Ticket window seconds for prize #${index + 1}`}
                          className="w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-sm tabular-nums text-white outline-none placeholder:text-white/30 focus:border-brand-pink/60"
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          ref={(el) => {
                            if (el) priceInputRefs.current.set(item.key, el);
                            else priceInputRefs.current.delete(item.key);
                          }}
                          type="number"
                          min={0}
                          step="0.01"
                          disabled={!prizeQueueEditable}
                          value={item.priceForWinner}
                          onChange={(e) => updateLobbyItem(item.key, { priceForWinner: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && prizeQueueEditable) {
                              e.preventDefault();
                              addLobbyItemWithFocus();
                            }
                          }}
                          aria-label={`Winner price for prize #${index + 1}`}
                          className="w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-sm tabular-nums text-white outline-none focus:border-brand-pink/60"
                          title={
                            productId && productsById.get(productId)
                              ? `Product price: ${rm(productsById.get(productId)!.price)}`
                              : undefined
                          }
                        />
                      </td>
                      {prizeQueueEditable && (
                        <td className="px-3 py-2 align-top">
                          <button
                            type="button"
                            className="rounded-lg p-2 text-white/40 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-30"
                            onClick={() => removeLobbyItem(item.key)}
                            disabled={!removable}
                            title={
                              (item.ticketCount ?? 0) > 0
                                ? `${item.ticketCount} ticket${item.ticketCount === 1 ? "" : "s"} entered — remove not allowed`
                                : item.status && item.status !== "PENDING"
                                  ? "Prize already opened for entries"
                                  : undefined
                            }
                            aria-label={`Remove prize #${index + 1}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <RightSidebarPanel
        open={!!monitorId}
        wide
        title={
          monitor && isRafflePreviewStatus(monitor.raffle.status)
            ? "Raffle preview"
            : (monitor?.raffle.title ?? "Raffle monitor")
        }
        subtitle={
          monitor
            ? isRafflePreviewStatus(monitor.raffle.status)
              ? `${monitor.raffle.title} · ${monitor.ticketCount} tickets · ${monitor.raffle.mode === "LOBBY" ? "Lobby" : "Single"}`
              : `${monitor.ticketCount} tickets · ${monitor.viewers} viewers · ${monitor.raffle.mode === "LOBBY" ? "Lobby" : "Single"}`
            : undefined
        }
        onClose={() => setMonitorId(null)}
        footer={
          monitorId ? (
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/os?raffle=${monitorId}`)}>
                Open in OS
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setMonitorId(null)}>
                Close
              </Button>
            </div>
          ) : undefined
        }
      >
        {monitorLoading && !monitor ? (
          <p className="text-center text-xs text-white/50">Loading monitor…</p>
        ) : monitor ? (
          <div className="sidebar-form">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-line bg-white/[0.02] p-3 text-center">
                <p className="text-[10px] uppercase text-white/40">Entries</p>
                <p className="font-display text-lg font-bold tabular-nums">{monitor.ticketCount}</p>
                {monitor.maxTicketNo != null && monitor.maxTicketNo > monitor.ticketCount && (
                  <p className="mt-1 text-[10px] text-amber-300/80">
                    Highest # is {monitor.maxTicketNo} — {monitor.maxTicketNo - monitor.ticketCount} failed attempt
                    {monitor.maxTicketNo - monitor.ticketCount === 1 ? "" : "s"} burned numbers during outages
                  </p>
                )}
              </div>
              <div className="rounded-xl border border-line bg-white/[0.02] p-3 text-center">
                <p className="text-[10px] uppercase text-white/40">Viewers</p>
                <p className="font-display text-lg font-bold tabular-nums">{monitor.viewers}</p>
              </div>
              <div className="rounded-xl border border-line bg-white/[0.02] p-3 text-center">
                <p className="text-[10px] uppercase text-white/40">Status</p>
                <Badge color={statusColor(monitor.raffle.status)}>{monitor.raffle.status}</Badge>
              </div>
            </div>

            {isRafflePreviewStatus(monitor.raffle.status) && monitor.raffle.mode === "SINGLE" && (
              <div className="rounded-xl border border-brand-pink/30 bg-brand-pink/10 p-3">
                <p className="text-[10px] uppercase text-white/40">Winner</p>
                {(() => {
                  const winner = monitor.tickets.find(
                    (t) => t.userId === monitor.raffle.winnerUserId && !t.forfeited,
                  );
                  return winner ? (
                    <>
                      <p className="font-display text-lg font-semibold">
                        <span className="font-mono text-brand-pink">#{winner.ticketNo}</span> {winner.name}
                      </p>
                      {monitor.raffle.drawnAt && (
                        <p className="mt-1 text-[10px] text-white/45">Drawn {fmtDate(monitor.raffle.drawnAt)}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-white/60">No confirmed winner yet</p>
                  );
                })()}
              </div>
            )}

            {monitor.raffle.mode === "LOBBY" && monitor.raffle.items && monitor.raffle.items.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">Draws</h4>
                <ul className="max-h-[min(70vh,32rem)] space-y-1 overflow-y-auto">
                  {monitor.raffle.items.map((item, idx) => {
                    const itemId = item.id ?? `idx-${idx}`;
                    const itemTickets =
                      item.id != null
                        ? monitor.tickets.filter((t) => t.raffleItemId === item.id)
                        : [];
                    const expanded = expandedDraws.has(itemId);
                    const isActive =
                      monitor.raffle.currentItemIndex === idx && monitor.raffle.status === "DRAWING";
                    const winnerNos = monitorWinnerTicketNos(monitor);
                    return (
                      <li
                        key={itemId}
                        className={`overflow-hidden rounded-lg border ${
                          isActive ? "border-brand-pink/50 bg-brand-pink/5" : "border-line/60"
                        }`}
                      >
                        <button
                          type="button"
                          className="flex w-full items-start justify-between gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-hover/40"
                          onClick={() =>
                            setExpandedDraws((prev) => {
                              const next = new Set(prev);
                              if (next.has(itemId)) next.delete(itemId);
                              else next.add(itemId);
                              return next;
                            })
                          }
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">
                              <span className="font-mono text-brand-pink">#{idx + 1}</span>{" "}
                              {item.product?.name ?? "Item"} · {item.winnerCount} winner
                              {item.winnerCount !== 1 ? "s" : ""}
                            </p>
                            <p className="mt-0.5 text-[10px] text-white/45">
                              {itemTickets.length} ticket{itemTickets.length !== 1 ? "s" : ""}
                              {item.winners && item.winners.length > 0
                                ? ` · ${item.winners.map((w) => `#${w.ticketNo} ${w.name}`).join(" · ")}`
                                : ""}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <Badge color={statusColor(item.status ?? "PENDING")}>{item.status ?? "PENDING"}</Badge>
                            <ChevronDown
                              size={14}
                              className={`text-white/45 transition-transform ${expanded ? "rotate-180" : ""}`}
                            />
                          </div>
                        </button>
                        {expanded && (
                          <div className="border-t border-line/60 px-2 pb-2 pt-1">
                            {itemTickets.length === 0 ? (
                              <p className="px-1 py-3 text-center text-[10px] text-white/40">No entries for this draw yet</p>
                            ) : (
                              <ul className="space-y-1">
                                {itemTickets.map((t) => {
                                  const isWinner = winnerNos.has(t.ticketNo);
                                  return (
                                    <li
                                      key={t.ticketNo}
                                      className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${
                                        t.forfeited
                                          ? "border-line/60 opacity-50 line-through"
                                          : isWinner
                                            ? "border-brand-pink/40 bg-brand-pink/10"
                                            : "border-line/40 bg-surface/40"
                                      }`}
                                    >
                                      <div className="min-w-0">
                                        <span className="font-mono text-brand-pink">#{t.ticketNo}</span>{" "}
                                        <span className="font-medium">{t.name}</span>
                                        {isWinner && !t.forfeited && (
                                          <span className="ml-1.5 inline-flex">
                                            <Badge color={statusColor("PAID")}>Winner</Badge>
                                          </span>
                                        )}
                                        <p className="truncate text-[10px] text-white/40">{t.email}</p>
                                      </div>
                                      <span className="shrink-0 text-white/40">{timeAgo(t.at)}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {monitor.raffle.mode !== "LOBBY" && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">Participants</h4>
                {monitor.tickets.length === 0 ? (
                  <p className="py-6 text-center text-xs text-white/40">No entries yet</p>
                ) : (
                  <ul className="max-h-80 space-y-1 overflow-y-auto">
                    {monitor.tickets.map((t) => {
                      const isWinner = monitorWinnerTicketNos(monitor).has(t.ticketNo);
                      return (
                        <li
                          key={t.ticketNo}
                          className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
                            t.forfeited
                              ? "border-line/60 opacity-50 line-through"
                              : isWinner
                                ? "border-brand-pink/40 bg-brand-pink/10"
                                : "border-line/60"
                          }`}
                        >
                          <div className="min-w-0">
                            <span className="font-mono text-brand-pink">#{t.ticketNo}</span>{" "}
                            <span className="font-medium">{t.name}</span>
                            {isWinner && !t.forfeited && (
                              <span className="ml-1.5 inline-flex">
                                <Badge color={statusColor("PAID")}>Winner</Badge>
                              </span>
                            )}
                            <p className="truncate text-[10px] text-white/40">{t.email}</p>
                          </div>
                          <span className="shrink-0 text-white/40">{timeAgo(t.at)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        ) : null}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
